/** A range of days as local ISO dates (yyyy-mm-dd); either end may be open. */
export interface DateRange {
  from?: string;
  to?: string;
}

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
const toLocalISO = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = (): string => toLocalISO(new Date());
export const daysAgoISO = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalISO(d);
};

export type DatePreset = 'today' | 'last_7_days' | 'last_30_days';

/** Open-ended "since" ranges; the key doubles as the label's i18n key. */
export const DATE_PRESETS: Array<{ key: DatePreset; from: () => string }> = [
  { key: 'today', from: todayISO },
  { key: 'last_7_days', from: () => daysAgoISO(7) },
  { key: 'last_30_days', from: () => daysAgoISO(30) },
];

export const activeDatePreset = (range: DateRange): DatePreset | undefined =>
  range.to ? undefined : DATE_PRESETS.find((preset) => preset.from() === range.from)?.key;

const shortDate = (iso: string, locale: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(locale || undefined, { day: 'numeric', month: 'short' });

/** "So'nggi 30 kun" for a preset, "3 Sep – 12 Sep" for a custom range, undefined when unset. */
export const dateRangeLabel = (range: DateRange, t: (key: string) => string, locale: string): string | undefined => {
  if (!range.from && !range.to) return undefined;
  const preset = activeDatePreset(range);
  if (preset) return t(preset);
  const from = range.from ? shortDate(range.from, locale) : '';
  const to = range.to ? shortDate(range.to, locale) : '';
  return `${from} – ${to}`.trim();
};
